(function (factory) {
  if (typeof module === 'object' && module.exports) module.exports = { createController: factory };
  else {
    function start() {
      if (!window.DiarrabluShipAid) window.DiarrabluShipAid = factory(window);
      window.DiarrabluShipAid.refresh();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(function createController(win, options) {
  'use strict';
  options = options || {};
  var doc = win.document;
  var choiceKey = 'diarrablu-shipaid-choice';
  var sharedChoiceKey = choiceKey + '-shared';
  var panels = new Map();
  var originalButtons = new Set();
  var cart = null;
  var quote = null;
  var message = '';
  var signature = '';
  var failedSignature = '';
  var preparedMerchandise = '';
  var queue = Promise.resolve();
  var refreshPromise = null;
  var pending = 0;
  var blocking = 0;
  var destroyed = false;
  var timer = null;
  var poll = null;
  var uid = 0;
  var inMemoryChoice = null;
  var pendingChoice = null;
  var choiceVersion = 0;
  var root = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
  var events = ['shipaid:widget:loaded', 'shipaid:cart:updated', 'cart:change', 'cart:updated', 'cart:refresh', 'cart-drawer:refreshed'];

  function readChoice() {
    try {
      var shared = JSON.parse(win.localStorage.getItem(sharedChoiceKey) || 'null');
      if (shared && cartIdentifier() && shared.cart === cartIdentifier() && (shared.choice === 'on' || shared.choice === 'off')) return shared.choice === 'on';
    } catch (_) {}
    try {
      var saved = win.sessionStorage.getItem(choiceKey);
      if (saved === 'off' || saved === 'on') return saved === 'on';
      if (win.sessionStorage.getItem('shipaid-opt-out-by-customer') === 'true') return false;
    } catch (_) {}
    return inMemoryChoice === null ? true : inMemoryChoice;
  }

  function saveChoice(on) {
    inMemoryChoice = on;
    try {
      if (cartIdentifier()) win.localStorage.setItem(sharedChoiceKey, JSON.stringify({ cart: cartIdentifier(), choice: on ? 'on' : 'off' }));
    } catch (_) {}
    try {
      win.sessionStorage.setItem(choiceKey, on ? 'on' : 'off');
      if (on) win.sessionStorage.removeItem('shipaid-opt-out-by-customer');
      else win.sessionStorage.setItem('shipaid-opt-out-by-customer', 'true');
    } catch (_) {}
  }

  function cartIdentifier() {
    // Shopify's token can contain an authorization suffix; never persist that part.
    return cart && typeof cart.token === 'string' ? cart.token.split('?')[0] : '';
  }

  function protectionLines(value) {
    return (value && value.items || []).filter(function (item) { return item.handle === 'shipaid-protection'; });
  }

  function hasMerchandise(value) {
    return (value && value.items || []).some(function (item) { return item.handle !== 'shipaid-protection' && item.quantity > 0; });
  }

  function cartSignature(value) {
    return JSON.stringify([value.currency, value.total_price, value.items.map(function (item) { return [item.key, item.quantity, item.final_line_price]; })]);
  }

  function merchandiseSignature(value) {
    var protectionTotal = protectionLines(value).reduce(function (sum, item) { return sum + item.final_line_price; }, 0);
    return JSON.stringify([value.currency, value.total_price - protectionTotal, value.items.filter(function (item) {
      return item.handle !== 'shipaid-protection';
    }).map(function (item) { return [item.key, item.variant_id, item.quantity, item.final_line_price]; })]);
  }

  function money(cents) {
    return new Intl.NumberFormat(doc.documentElement.lang || 'en-US', { style: 'currency', currency: cart && cart.currency || 'USD' }).format(cents / 100);
  }

  async function fetchCart() {
    var abort = new win.AbortController();
    var timeout = win.setTimeout(function () { abort.abort(); }, 12000);
    try {
      var response = await win.fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', signal: abort.signal });
      if (!response.ok) throw new Error('Cart unavailable');
      var value = await response.json();
      if (!Array.isArray(value.items) || !Number.isFinite(value.total_price)) throw new Error('Invalid cart');
      return value;
    } finally { win.clearTimeout(timeout); }
  }

  function readQuote(widget) {
    var button = widget.querySelector('[variant]');
    var price = widget.querySelector('.protection-value');
    var variant = button && button.getAttribute('variant');
    if (!variant || !/^\d+$/.test(variant) || Number(variant) <= 0 || !price) return null;
    var copy = price.cloneNode(true);
    copy.querySelectorAll('slot, svg').forEach(function (node) { node.remove(); });
    var amount = copy.textContent.replace(/\s+/g, ' ').trim();
    if (!/\d/.test(amount)) return null;
    return { variant: variant, price: amount };
  }

  function widgets() {
    return Array.from(doc.querySelectorAll('shipaid-widget[use-shipaid-checkout]')).filter(function (widget) {
      return typeof widget.updateCart === 'function' && typeof widget.updateProtection === 'function' && typeof widget.addProtection === 'function' && typeof widget.removeProtection === 'function';
    });
  }

  function removePanel(widget, panel) {
    panel.remove();
    widget.classList.remove('diarrablu-shipaid-engine');
    panels.delete(widget);
  }

  function resetPanels() {
    panels.forEach(function (panel, widget) { removePanel(widget, panel); });
    originalButtons.forEach(function (button) { button.classList.remove('diarrablu-shipaid-original'); });
    originalButtons.clear();
  }

  function mountInformation(panel, widget, id) {
    var row = panel.querySelector('.diarrablu-shipaid__row');
    var info = doc.createElement('button');
    info.type = 'button';
    info.className = 'diarrablu-shipaid__info';
    info.setAttribute('aria-label', 'About Shipping Guarantee by ShipAid');
    info.setAttribute('aria-expanded', 'false');
    info.setAttribute('aria-controls', id + '-details');
    info.innerHTML = '<span aria-hidden="true">i</span>';
    row.insertBefore(info, row.querySelector('.diarrablu-shipaid__switch'));
    var details = doc.createElement('div');
    details.id = id + '-details';
    details.className = 'diarrablu-shipaid__details';
    details.hidden = true;
    var list = doc.createElement('ul');
    var benefits = widget.querySelectorAll('.shipaid-benefits li');
    var descriptions = Array.from(benefits).map(function (benefit) { return benefit.textContent.trim(); }).filter(Boolean);
    if (!descriptions.length) descriptions = ['Delivery guarantee against loss, theft, or damage.'];
    descriptions.forEach(function (description) {
      var item = doc.createElement('li');
      item.textContent = description;
      list.appendChild(item);
    });
    details.appendChild(list);
    var more = doc.createElement('button');
    more.type = 'button';
    more.className = 'diarrablu-shipaid__more';
    more.textContent = 'More info';
    more.addEventListener('click', function () {
      var nativeLink = widget.querySelector('.shipaid-benefits__link');
      if (nativeLink) nativeLink.click();
      else win.dispatchEvent(new win.CustomEvent('shipaid:learn-more:opened', { detail: { source: 'shipaid-widget' } }));
    });
    details.appendChild(more);
    row.insertAdjacentElement('afterend', details);
    info.addEventListener('click', function () {
      details.hidden = !details.hidden;
      info.setAttribute('aria-expanded', String(!details.hidden));
    });
  }

  function mountPanels() {
    panels.forEach(function (panel, widget) {
      if (!widget.isConnected || !panel.isConnected) removePanel(widget, panel);
    });
    if (!cart || !quote || !hasMerchandise(cart)) return;
    widgets().forEach(function (widget) {
      if (panels.has(widget) || !readQuote(widget)) return;
      var panel = doc.createElement('div');
      panel.className = 'diarrablu-shipaid';
      var id = 'diarrablu-shipaid-' + (++uid);
      panel.innerHTML = '<div class="diarrablu-shipaid__row"><label class="diarrablu-shipaid__description" for="' + id + '"><span class="diarrablu-shipaid__logo" aria-hidden="true"></span><span>Shipping Guarantee by ShipAid <span class="diarrablu-shipaid__price"></span></span></label><label class="diarrablu-shipaid__switch" for="' + id + '"><input id="' + id + '" type="checkbox" role="switch" aria-label="Shipping Guarantee by ShipAid"><span class="diarrablu-shipaid__track"></span></label></div><p class="diarrablu-shipaid__error" role="alert" hidden></p><button class="diarrablu-shipaid__retry" type="button" hidden>Try again</button><button class="diarrablu-shipaid__checkout button" type="button">CHECKOUT</button>';
      mountInformation(panel, widget, id);
      var logo = widget.querySelector('.protection-text img, .protection-text svg');
      if (logo) {
        var icon = logo.cloneNode(true);
        icon.removeAttribute('id'); icon.setAttribute('aria-hidden', 'true');
        if (icon.tagName.toLowerCase() === 'img') icon.alt = '';
        panel.querySelector('.diarrablu-shipaid__logo').appendChild(icon);
      }
      panel.querySelector('input').addEventListener('change', function (event) { setProtection(event.target.checked); });
      panel.querySelector('.diarrablu-shipaid__checkout').addEventListener('click', checkout);
      panel.querySelector('.diarrablu-shipaid__retry').addEventListener('click', function () { setProtection(readChoice()); });
      widget.insertAdjacentElement('afterend', panel);
      widget.classList.add('diarrablu-shipaid-engine');
      panels.set(widget, panel);
    });
    if (panels.size) {
      doc.querySelectorAll('[name="checkout"]').forEach(function (button) {
        if (button.closest('shipaid-widget, .diarrablu-shipaid')) return;
        button.classList.add('diarrablu-shipaid-original');
        originalButtons.add(button);
      });
    }
  }

  function render() {
    mountPanels();
    if (cart) {
      doc.querySelectorAll('[data-shipaid-cart-total]').forEach(function (total) {
        setText(total, money(cart.total_price) + ' ' + cart.currency);
      });
      doc.querySelectorAll('cart-count').forEach(function (count) { setText(count, String(cart.item_count)); });
      doc.querySelectorAll('cart-dot').forEach(function (dot) { dot.classList.toggle('is-visible', cart.item_count > 0); });
    }
    var lines = protectionLines(cart);
    var checked = lines.length > 0;
    var price = checked ? money(lines.reduce(function (sum, item) { return sum + item.final_line_price; }, 0)) : quote && quote.price;
    panels.forEach(function (panel) {
      panel.setAttribute('aria-busy', blocking > 0 ? 'true' : 'false');
      var toggle = panel.querySelector('input');
      toggle.checked = pendingChoice === null ? checked : pendingChoice;
      toggle.disabled = blocking > 0;
      setText(panel.querySelector('.diarrablu-shipaid__price'), '+' + (price || '—'));
      var button = panel.querySelector('.diarrablu-shipaid__checkout');
      setText(button, cart ? 'CHECKOUT ' + money(cart.total_price) : 'CHECKOUT');
      button.disabled = blocking > 0 || !!message || !hasMerchandise(cart);
      var error = panel.querySelector('.diarrablu-shipaid__error');
      setText(error, message);
      error.hidden = !message;
      panel.querySelector('.diarrablu-shipaid__retry').hidden = !message;
      panel.querySelector('.diarrablu-shipaid__retry').disabled = blocking > 0;
    });
  }

  function setText(element, text) {
    if (element.textContent !== text) element.textContent = text;
  }

  function enqueue(operation, quiet) {
    var blocks = !quiet;
    pending++;
    if (blocks) blocking++;
    render();
    var result = queue.then(async function () {
      if (destroyed) return;
      try { await operation(function () { if (!blocks) { blocks = true; blocking++; render(); } }); }
      catch (_) {
        message = 'We could not update your shipping guarantee. Please try again before checkout.';
        failedSignature = cart ? cartSignature(cart) : '';
      }
      finally { pending--; if (blocks) blocking--; if (!destroyed) render(); }
    });
    queue = result.catch(function () {});
    return result;
  }

  async function reconcile(force, desired, markBusy) {
    var next = await fetchCart();
    var before = cartSignature(next);
    cart = next;
    var available = widgets();
    // V1 removes its quote when the last merchandise item is removed. Its public
    // cleanup still works, so do not require a rendered quote for an orphan line.
    if (!hasMerchandise(cart) && protectionLines(cart).length && available.length) {
      if (markBusy) markBusy();
      var cleanupWidget = available[0];
      for (var cleanupAttempt = 0; cleanupAttempt < 5; cleanupAttempt++) {
        await cleanupWidget.updateCart();
        await cleanupWidget.updateProtection();
        if (cleanupWidget.updateComplete) await cleanupWidget.updateComplete;
        cart = await fetchCart();
        if (!protectionLines(cart).length || hasMerchandise(cart)) break;
        if (cleanupAttempt === 4) throw new Error('The empty cart guarantee could not be removed');
        await new Promise(function (resolve) { win.setTimeout(resolve, 150); });
      }
      if (!hasMerchandise(cart)) {
        quote = null; message = ''; failedSignature = ''; signature = cartSignature(cart);
        resetPanels();
        if (before !== signature) notifyTheme();
        return;
      }
    }
    var widget = available.find(function (candidate) { return readQuote(candidate); });
    if (!widget) {
      if (!hasMerchandise(cart)) resetPanels();
      else if (force && panels.size) throw new Error('Shipping guarantee is not ready');
      return;
    }
    if (!force && before === signature && !message) { quote = readQuote(widget); return; }
    if (!force && before === failedSignature) return;
    if (markBusy) markBusy();
    var needsPreparation = merchandiseSignature(cart) !== preparedMerchandise;
    if (!force && signature && before !== signature && !needsPreparation) {
      // Tabs share Shopify's cart, but sessionStorage does not. A background check
      // adopts another tab's verified selection instead of undoing it.
      desired = Boolean(protectionLines(cart).length);
      saveChoice(desired);
    }
    if (needsPreparation) {
      // Enrich exclusions and recalculate only when merchandise or currency changed.
      await widget.updateCart();
      await widget.updateProtection();
      if (widget.updateComplete) await widget.updateComplete;
      cart = await fetchCart();
    }
    if (!hasMerchandise(cart)) {
      quote = null; message = ''; signature = cartSignature(cart); resetPanels();
      if (before !== signature) notifyTheme();
      return;
    }
    quote = readQuote(widget);
    if (!quote) { resetPanels(); return; }
    if (needsPreparation && typeof widget.calculateProtectionTotal === 'function' && !(await widget.calculateProtectionTotal())) {
      quote = null; message = ''; resetPanels(); signature = cartSignature(cart); return;
    }
    preparedMerchandise = merchandiseSignature(cart);
    if (typeof desired !== 'boolean') desired = readChoice();
    if (Boolean(protectionLines(cart).length) !== desired) {
      // These public V1 mutation methods retain pricing/subscription behavior without
      // _handleRefresh's section replacement. The compact controls update in place.
      var quietMutation = desired ? typeof widget.addCartProtectionVariant === 'function' : typeof widget.updateCartProtectionVariant === 'function';
      if (desired) {
        if (quietMutation) await widget.addCartProtectionVariant();
        else await widget.addProtection();
      } else if (quietMutation) {
        for (var line of protectionLines(cart)) await widget.updateCartProtectionVariant(0, line);
      } else await widget.removeProtection();
      if (quietMutation) {
        await widget.updateCart();
        await widget.updateProtection();
        if (widget.updateComplete) await widget.updateComplete;
        quote = readQuote(widget) || quote;
      }
      cart = await fetchCart();
      if (Boolean(protectionLines(cart).length) !== desired) throw new Error('Protection mutation was not applied');
    }
    // Native widget effects may still be switching a stale fee variant after updateCart resolves.
    // Never enable checkout until the actual line matches the quoted variant and quantity.
    for (var attempt = 0; attempt < 5; attempt++) {
      var lines = protectionLines(cart);
      var correct = desired ? lines.length === 1 && String(lines[0].variant_id) === quote.variant && lines[0].quantity === 1 : lines.length === 0;
      if (correct) break;
      if (attempt === 4) throw new Error('Protection fee is still updating');
      await new Promise(function (resolve) { win.setTimeout(resolve, 150); });
      await widget.updateCart();
      await widget.updateProtection();
      if (widget.updateComplete) await widget.updateComplete;
      quote = readQuote(widget) || quote;
      cart = await fetchCart();
    }
    message = ''; failedSignature = ''; signature = cartSignature(cart);
    saveChoice(Boolean(protectionLines(cart).length));
    // Guarantee-only mutations do not change visible merchandise. Refreshing the
    // entire Prestige drawer here would destroy the toggle and restart its widgets.
  }

  function notifyTheme() {
    // Prestige's cart:change requires bundled section HTML. cart:refresh fetches it safely.
    doc.dispatchEvent(new win.CustomEvent('cart:refresh', { detail: { source: 'diarrablu-shipaid' } }));
  }

  function refresh() {
    if (destroyed) return Promise.resolve();
    if (refreshPromise) return refreshPromise;
    refreshPromise = enqueue(function (markBusy) { return reconcile(false, undefined, markBusy); }, true).finally(function () { refreshPromise = null; });
    return refreshPromise;
  }

  function setProtection(on) {
    saveChoice(Boolean(on));
    pendingChoice = Boolean(on);
    var version = ++choiceVersion;
    return enqueue(function () { return reconcile(true, Boolean(on)); }).finally(function () {
      if (version === choiceVersion) pendingChoice = null;
      if (!destroyed) render();
    });
  }

  function checkout() {
    return enqueue(async function () {
      await reconcile(true, readChoice());
      if (message || !hasMerchandise(cart) || !panels.size) return;
      var url = root + 'checkout';
      if (protectionLines(cart).length) url += '?attributes%5B_shipaid-internal%5D=1';
      // Cart is already reconciled; legacy updates[variant] query parameters could undo an opt-out.
      if (options.navigate) options.navigate(url);
      else win.location.assign(url);
    });
  }

  function schedule(event) {
    if (destroyed || pending || event && event.detail && event.detail.source === 'diarrablu-shipaid') return;
    if (event && event.detail && event.detail.cart && Array.isArray(event.detail.cart.items) && cartSignature(event.detail.cart) === signature) {
      render();
      return;
    }
    win.clearTimeout(timer);
    timer = win.setTimeout(refresh, options.debounceDelay == null ? 100 : options.debounceDelay);
  }

  events.forEach(function (name) { doc.addEventListener(name, schedule); });
  win.addEventListener('pageshow', schedule);
  var observer = new win.MutationObserver(function (records) {
    if (pending) return;
    if (records.some(function (record) {
      if (record.target.nodeType === 1 && record.target.closest('.diarrablu-shipaid')) return false;
      return Array.from(record.addedNodes).concat(Array.from(record.removedNodes)).some(function (node) {
        return node.nodeType === 1 && (node.matches('shipaid-widget') || node.querySelector('shipaid-widget'));
      }) || record.target.nodeType === 1 && !!record.target.closest('shipaid-widget');
    })) schedule();
  });
  observer.observe(doc.documentElement, { childList: true, subtree: true });
  var pollInterval = options.pollInterval == null ? 3000 : options.pollInterval;
  if (pollInterval) poll = win.setInterval(function () { if (!doc.hidden && widgets().length) refresh(); }, pollInterval);

  return {
    refresh: refresh,
    setProtection: setProtection,
    checkout: checkout,
    destroy: function () {
      destroyed = true;
      observer.disconnect(); win.clearTimeout(timer); win.clearInterval(poll);
      events.forEach(function (name) { doc.removeEventListener(name, schedule); });
      win.removeEventListener('pageshow', schedule);
      resetPanels();
    }
  };
});
